import React, {
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, InputPrimitive } from "@xsolla/xui-primitives";
import { formatSegment, parseSegmentInput } from "./utils";

interface TimeSegmentProps {
  value: number | null;
  onChange: (value: number | null) => void;
  onAdvance: () => void;
  onRetreat: () => void;
  min: number;
  max: number;
  onPasteTime?: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  fontSize: number;
  textColor: string;
  placeholderColor: string;
  focusColor: string;
  "aria-label"?: string;
  testID?: string;
}

export const TimeSegment = forwardRef<{ focus: () => void }, TimeSegmentProps>(
  (
    {
      value,
      onChange,
      onAdvance,
      onRetreat,
      min,
      max,
      onPasteTime,
      disabled = false,
      placeholder = "--",
      fontSize,
      textColor,
      placeholderColor,
      focusColor,
      "aria-label": ariaLabel,
      testID,
    },
    ref
  ) => {
    const inputRef = useRef<any>(null);
    const [editBuffer, setEditBuffer] = useState<string>("");
    const [isFocused, setIsFocused] = useState(false);
    const committedRef = useRef(false);
    const freshFocusRef = useRef(false);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus?.(),
    }));

    const displayValue = isFocused
      ? editBuffer
      : value !== null
        ? formatSegment(value)
        : "";

    const commitValue = useCallback(
      (text: string) => {
        const parsed = parseSegmentInput(text);
        if (parsed !== null) {
          const clamped = Math.min(max, Math.max(min, parsed));
          onChange(clamped);
        }
      },
      [onChange, min, max]
    );

    const handleFocus = () => {
      setIsFocused(true);
      setEditBuffer(value !== null ? formatSegment(value) : "");
      committedRef.current = false;
      freshFocusRef.current = true;
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (!committedRef.current && editBuffer) {
        commitValue(editBuffer);
      }
      committedRef.current = false;
      setEditBuffer("");
    };

    const extractTypedDigit = (newText: string, oldText: string): string => {
      const newDigits = newText.replace(/\D/g, "");
      const oldDigits = oldText.replace(/\D/g, "");
      if (newDigits.length <= oldDigits.length) return "";
      let remaining = newDigits;
      for (const ch of oldDigits) {
        const idx = remaining.indexOf(ch);
        if (idx !== -1) {
          remaining = remaining.slice(0, idx) + remaining.slice(idx + 1);
        }
      }
      return remaining.charAt(0);
    };

    const handleChangeText = (text: string) => {
      let digitsOnly = text.replace(/\D/g, "");
      if (freshFocusRef.current || committedRef.current) {
        const typed = extractTypedDigit(text, displayValue);
        freshFocusRef.current = false;
        committedRef.current = false;
        if (!typed) return;
        digitsOnly = typed;
      } else {
        digitsOnly = digitsOnly.slice(-2);
      }
      setEditBuffer(digitsOnly);
      if (digitsOnly.length === 2) {
        committedRef.current = true;
        commitValue(digitsOnly);
        onAdvance();
      } else if (digitsOnly.length === 1) {
        const d = parseInt(digitsOnly, 10);
        if (d * 10 > max) {
          committedRef.current = true;
          commitValue(digitsOnly);
          onAdvance();
        }
      }
    };

    const handlePaste = (e: any) => {
      const text: string = e.clipboardData?.getData?.("text") ?? "";
      if (onPasteTime && text.includes(":")) {
        e.preventDefault();
        onPasteTime(text);
      }
    };

    const handleKeyDown = (e: any) => {
      const key = e.key || e.nativeEvent?.key;

      if (key === "ArrowUp") {
        e.preventDefault();
        const current = value ?? min;
        const next = current >= max ? min : current + 1;
        onChange(next);
        setEditBuffer(formatSegment(next));
        committedRef.current = true;
      } else if (key === "ArrowDown") {
        e.preventDefault();
        const current = value ?? min;
        const next = current <= min ? max : current - 1;
        onChange(next);
        setEditBuffer(formatSegment(next));
        committedRef.current = true;
      } else if (key === "ArrowRight") {
        onAdvance();
      } else if (key === "ArrowLeft") {
        onRetreat();
      } else if (key === "Backspace" && editBuffer.length === 0) {
        onChange(null);
        onRetreat();
      } else if (key === "Tab") {
        if (editBuffer) {
          commitValue(editBuffer);
        }
      } else if (key === "Enter") {
        if (editBuffer) {
          commitValue(editBuffer);
        }
        inputRef.current?.blur?.();
      }
    };

    return (
      <Box
        onPress={() => inputRef.current?.focus?.()}
        width={fontSize * 1.5}
        height={fontSize + 8}
        justifyContent="center"
        alignItems="center"
        backgroundColor={isFocused ? focusColor : "transparent"}
        borderRadius={4}
      >
        <InputPrimitive
          ref={inputRef}
          value={displayValue}
          placeholder={placeholder}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled}
          inputMode="numeric"
          color={textColor}
          fontSize={fontSize}
          placeholderTextColor={placeholderColor}
          role="spinbutton"
          aria-label={ariaLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value !== null ? value : undefined}
          aria-valuetext={value !== null ? formatSegment(value) : undefined}
          aria-disabled={disabled || undefined}
          data-testid={testID}
          style={{
            textAlign: "center",
            width: "100%",
            padding: 0,
            border: "none",
            outline: "none",
            boxShadow: "none",
            background: "transparent",
            caretColor: "transparent",
          }}
        />
      </Box>
    );
  }
);

TimeSegment.displayName = "TimeSegment";
